<script setup>
import { ref, computed } from 'vue'
import { Loader2, CheckCircle2 } from 'lucide-vue-next'
import { Button } from '@/components/ui/button'
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog'

const props = defineProps({
  open: { type: Boolean, default: false },
  features: { type: Array, default: () => [] },
  isUploading: { type: Boolean, default: false }
})

const emit = defineEmits(['close', 'cancel'])

// Chunk-level progress, fed by FileViewer via setChunkProgress(...).
const chunkIndex = ref(0)
const chunkCount = ref(1)
const uploaded = ref(0)
const total = ref(0)
const resourceCount = ref(0)

const percent = computed(() => {
  if (!total.value) return 0
  return Math.round((uploaded.value / total.value) * 100)
})

const isComplete = computed(() =>
  total.value > 0 && uploaded.value >= total.value && !props.isUploading
)

// setChunkProgress is the single source of truth for feature-phase state.
// The parent calls it with uploaded=0 at the start of every upload, which
// implicitly initializes the dialog — resourceCount is reset here too
// because setChunkProgress only fires during the feature phase, which
// always precedes the resource phase.
const setChunkProgress = ({ chunkIndex: ci, chunkCount: cc, uploaded: u, total: t }) => {
  chunkIndex.value = ci
  chunkCount.value = cc
  uploaded.value = u
  total.value = t
  resourceCount.value = 0
}

const setResourceProgress = (n) => {
  resourceCount.value = n
}

const handleClose = () => {
  if (props.isUploading) emit('cancel')
  else emit('close')
}

defineExpose({ setChunkProgress, setResourceProgress })
</script>

<template>
  <Dialog :open="open" @update:open="handleClose">
    <DialogContent class="max-w-md">
      <DialogHeader>
        <DialogTitle>{{ isComplete ? 'Upload complete' : 'Uploading features' }}</DialogTitle>
      </DialogHeader>

      <div class="py-6 space-y-4">
        <div class="flex items-center justify-center">
          <CheckCircle2 v-if="isComplete" class="w-10 h-10 text-green-500" />
          <Loader2 v-else class="w-10 h-10 text-primary animate-spin" />
        </div>

        <div class="text-center space-y-1">
          <p class="text-sm font-medium">
            {{ uploaded }} of {{ total }} features uploaded
          </p>
          <p v-if="chunkCount > 1" class="text-xs text-muted-foreground">
            Chunk {{ chunkIndex + 1 }} of {{ chunkCount }}
          </p>
          <p v-if="resourceCount > 0" class="text-xs text-muted-foreground">
            Uploading {{ resourceCount }} attached {{ resourceCount === 1 ? 'file' : 'files' }}…
          </p>
        </div>

        <div class="w-full bg-muted rounded-full h-2 overflow-hidden">
          <div
            class="bg-primary h-full transition-all duration-300 ease-out"
            :style="{ width: percent + '%' }"
          />
        </div>
      </div>

      <DialogFooter>
        <Button variant="outline" @click="handleClose">
          {{ isUploading ? 'Stop' : 'Close' }}
        </Button>
      </DialogFooter>
    </DialogContent>
  </Dialog>
</template>
